import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Button, TextField, Chip, Avatar,
  LinearProgress, MenuItem, Checkbox, FormControlLabel, Paper,
  IconButton, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { ArrowBack, ArrowForward, CameraAlt, ExpandMore, CheckCircle, Coffee } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import eventsService from '../../services/eventsService';
import httpClient from '../../utils/httpClient';
import { colors, designTokens } from '../../theme/theme';

const INTERESTS = [
  'Technology', 'Design', 'Marketing', 'Finance', 'Startups', 'AI/ML',
  'Product', 'Leadership', 'Sustainability', 'Creative Arts', 'Healthcare',
  'Education', 'Legal', 'Real Estate', 'Media', 'Gaming',
  'Food & Beverage', 'Travel', 'Fitness', 'Photography',
];

const INDUSTRIES = [
  'Technology', 'Finance & Banking', 'Marketing & Advertising', 'Design & Creative',
  'Healthcare', 'Education', 'Legal', 'Real Estate', 'Consulting',
  'Media & Entertainment', 'Government', 'Nonprofit', 'Retail',
  'Energy & Oil', 'Hospitality & Tourism', 'Other',
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data accumulated across steps
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [industry, setIndustry] = useState(user?.industry || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [interests, setInterests] = useState<string[]>(user?.interests || []);
  const [zoneId, setZoneId] = useState('');
  const [zones, setZones] = useState<{ id: string; name: string }[]>([]);
  const [safetyChecked, setSafetyChecked] = useState(false);

  // If already onboarded, redirect
  /* eslint-disable react-hooks/exhaustive-deps -- navigate is stable from React Router; we intentionally re-evaluate only on user changes. */
  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/', { replace: true });
    }
  }, [user]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Load zones for step 3
  useEffect(() => {
    eventsService.getZones().then(setZones).catch(() => { /* no-op */ });
  }, []);

  const totalSteps = 4;
  const progress = ((step + 1) / totalSteps) * 100;

  const toggleInterest = (interest: string) => {
    setInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarUrl(URL.createObjectURL(file));
    }
  };

  const handleComplete = async () => {
    if (!safetyChecked) { setError('Please acknowledge the safety commitment'); return; }
    setLoading(true);
    setError('');
    try {
      // Upload avatar first if selected
      let finalAvatarUrl = avatarUrl;
      if (avatarFile) {
        try {
          const formData = new FormData();
          formData.append('file', avatarFile);
          const { data } = await httpClient.post('/users/me/avatar', formData);
          finalAvatarUrl = data.avatar_url;
        } catch (err) {
          // expected: avatar upload is optional — onboarding continues without it.
          // User can update avatar later from profile.
          console.warn('[OnboardingWizard] avatar upload failed (non-blocking)', err);
        }
      }

      // Submit all onboarding data
      await httpClient.post('/users/me/onboarding', {
        first_name: firstName,
        last_name: lastName,
        bio: bio || undefined,
        industry: industry || undefined,
        interests: interests.length >= 3 ? interests : undefined,
        preferred_zone_id: zoneId || undefined,
        avatar_url: finalAvatarUrl || undefined,
      });

      await refreshUser();
      navigate('/', { replace: true });
    } catch (e: any) {
      console.warn('[OnboardingWizard] complete onboarding failed', e);
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return firstName.trim().length > 0 && lastName.trim().length > 0;
    if (step === 2) return interests.length >= 3;
    if (step === 3) return safetyChecked;
    return true;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.neutral[50], display: 'flex', flexDirection: 'column' }}>
      {/* Progress bar */}
      <LinearProgress variant="determinate" value={progress}
        sx={{ height: 4, bgcolor: colors.neutral[200], '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main } }} />

      {/* Step dots */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 2 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <Box key={i} sx={{
            width: 10, height: 10, borderRadius: '50%',
            bgcolor: i <= step ? colors.primary.main : colors.neutral[300],
            transition: 'background-color 0.3s',
          }} />
        ))}
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 3 }}>

        {/* ── Step 0: Welcome ── */}
        {step === 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: colors.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <Coffee sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 2 }}>
              Welcome to CoffeeMates
            </Typography>
            <Typography variant="h6" sx={{ color: colors.neutral[500], mb: 4, lineHeight: 1.6 }}>
              Meet interesting people over coffee.<br />
              Small groups. Real conversations. Every week.
            </Typography>
            <Button variant="contained" size="large" endIcon={<ArrowForward />}
              onClick={() => setStep(1)}
              sx={{ py: 1.5, px: 4, borderRadius: 3, bgcolor: colors.primary.main, fontSize: '1.1rem' }}>
              Let's set you up
            </Button>
          </Box>
        )}

        {/* ── Step 1: Profile + Photo ── */}
        {step === 1 && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Who are you?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Help your future tablemates know who they're meeting.
            </Typography>

            {/* Avatar */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar src={avatarUrl} sx={{ width: 100, height: 100, bgcolor: colors.primary.light, fontSize: 36 }}>
                  {firstName ? firstName[0] : '?'}
                </Avatar>
                <IconButton component="label" aria-label="Upload profile photo" sx={{
                  position: 'absolute', bottom: -4, right: -4,
                  bgcolor: colors.primary.main, color: 'white', width: 32, height: 32,
                  '&:hover': { bgcolor: colors.primary.dark },
                }}>
                  <CameraAlt sx={{ fontSize: 16 }} />
                  <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
                </IconButton>
              </Box>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: colors.neutral[400] }}>
                People are 3x more likely to join when they see who's coming
              </Typography>
            </Box>

            <TextField fullWidth label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)}
              required sx={{ mb: 2 }} />
            <TextField fullWidth label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)}
              required sx={{ mb: 2 }} />
            <TextField fullWidth label="Short Bio" value={bio} onChange={e => setBio(e.target.value.slice(0, 150))}
              multiline rows={2} placeholder="What do you do? What excites you?"
              helperText={`${bio.length}/150`} sx={{ mb: 2 }} />
            <TextField fullWidth select label="Industry" value={industry} onChange={e => setIndustry(e.target.value)}
              sx={{ mb: 2 }}>
              <MenuItem value="">Select your industry</MenuItem>
              {INDUSTRIES.map(ind => <MenuItem key={ind} value={ind}>{ind}</MenuItem>)}
            </TextField>
          </Box>
        )}

        {/* ── Step 2: Interests + Zone ── */}
        {step === 2 && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              What are you into?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pick at least 3 interests — these help us find the right events and people for you.
            </Typography>
            <Chip label={`${interests.length}/3 minimum`} size="small"
              sx={{ mb: 2, bgcolor: interests.length >= 3 ? colors.success.subtle : colors.warning.subtle,
                color: interests.length >= 3 ? colors.success.dark : colors.warning.dark }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {INTERESTS.map(interest => (
                <Chip key={interest} label={interest} clickable
                  onClick={() => toggleInterest(interest)}
                  sx={{
                    bgcolor: interests.includes(interest) ? colors.primary.main : colors.neutral[100],
                    color: interests.includes(interest) ? 'white' : colors.neutral[700],
                    fontWeight: interests.includes(interest) ? 600 : 400,
                    '&:hover': { bgcolor: interests.includes(interest) ? colors.primary.dark : colors.neutral[200] },
                  }} />
              ))}
            </Box>

            <TextField fullWidth select label="Preferred Zone (optional)" value={zoneId}
              onChange={e => setZoneId(e.target.value)} sx={{ mb: 2 }}>
              <MenuItem value="">No preference</MenuItem>
              {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
            </TextField>
          </Box>
        )}

        {/* ── Step 3: Safety ── */}
        {step === 3 && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Our Safety Promise
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              CoffeeMates brings strangers together. We take safety seriously.
            </Typography>

            <Paper sx={{ p: 3, bgcolor: colors.primary.subtle, borderRadius: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { title: 'Zero tolerance for harassment', desc: 'Any form of harassment results in immediate, permanent removal.' },
                  { title: 'No intoxication', desc: 'Showing up under the influence gets you banned from the platform.' },
                  { title: 'Respect everyone', desc: 'Threatening, abusive, or discriminatory behavior is not tolerated.' },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <CheckCircle sx={{ color: colors.primary.main, mt: 0.3 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Accordion sx={{ mb: 3, boxShadow: 'none', border: `1px solid ${colors.neutral[200]}` }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>View full safety policy</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  CoffeeMates operates a zero-tolerance policy for sexual harassment, physical assault,
                  stalking, and unwanted contact after events. These result in an immediate permanent ban
                  after a single verified report. Intoxication and disruptive behavior follow a graduated
                  system with warnings before permanent removal. All reports are reviewed within 24 hours
                  by our admin team. Reporters can remain anonymous. Banned users are blocked via phone,
                  email, and name matching to prevent re-registration.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <FormControlLabel
              control={<Checkbox checked={safetyChecked} onChange={e => setSafetyChecked(e.target.checked)}
                sx={{ color: colors.primary.main, '&.Mui-checked': { color: colors.primary.main } }} />}
              label={<Typography variant="body2" sx={{ fontWeight: 500 }}>
                I commit to being respectful and showing up when I commit
              </Typography>}
            />

            {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
          </Box>
        )}
      </Container>

      {/* Navigation buttons */}
      {step > 0 && (
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', bgcolor: colors.background.paper, borderTop: `1px solid ${designTokens.glass.border}` }}>
          <Button startIcon={<ArrowBack />} onClick={() => { setStep(s => s - 1); setError(''); }}
            sx={{ color: colors.neutral[600] }}>
            Back
          </Button>
          {step < 3 ? (
            <Button variant="contained" endIcon={<ArrowForward />}
              disabled={!canProceed()}
              onClick={() => setStep(s => s + 1)}
              sx={{ bgcolor: colors.primary.main }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" size="large"
              disabled={!canProceed() || loading}
              onClick={handleComplete}
              sx={{ bgcolor: colors.primary.main, px: 4 }}>
              {loading ? 'Setting up...' : 'Complete Setup'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
